import { z } from "zod";
import { parseMoney } from "./money";
import type { AnswerMap, FormContent, Question } from "./types";

const fullDateSchema = z.iso.date();

/** Comparisons are between scalar amounts or full calendar dates, never entries. */
export function canCompareAnswers(
  form: FormContent,
  question: Question,
  source: Question,
): boolean {
  const repeated = (q: Question) =>
    form.groups?.some((g) => g.id === q.groupId && g.repeat);

  if (question.id === source.id || repeated(question) || repeated(source)) {
    return false;
  }

  if (question.type === "money") {
    return source.type === "money";
  }

  return (
    question.type === "date" &&
    source.type === "date" &&
    !question.config?.monthDayOnly &&
    !question.config?.includeTime &&
    !source.config?.monthDayOnly &&
    !source.config?.includeTime
  );
}

/** February 29's anniversary is February 28 in a non-leap year. No time zones. */
function anniversary(date: string, years: number): string {
  const year = Number(date.slice(0, 4)) + years;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const suffix = date.slice(4) === "-02-29" && !leap ? "-02-28" : date.slice(4);
  return `${String(year).padStart(4, "0")}${suffix}`;
}

/** Both endpoints must be visible and valid; missing/invalid answers own their errors. */
export function answerComparisonIssues(
  form: FormContent,
  question: Question,
  visible: ReadonlyMap<string, Question>,
  answers: AnswerMap,
): string[] {
  const value = answers[question.id];

  if (value?.kind !== "text" || !value.value.trim()) {
    return [];
  }

  const issues: string[] = [];

  for (const rule of question.validation ?? []) {
    if (rule.kind !== "compareAnswers") {
      continue;
    }

    for (const comparison of rule.comparisons) {
      const source = visible.get(comparison.questionId);

      if (!source || !canCompareAnswers(form, question, source)) {
        continue;
      }

      const other = answers[source.id];

      if (other?.kind !== "text" || !other.value.trim()) {
        continue;
      }

      if (question.type === "money") {
        const amount = parseMoney(value.value);
        const bound = parseMoney(other.value);

        if (amount === undefined || bound === undefined) {
          continue;
        }

        if (comparison.op === "atLeast" ? amount < bound : amount > bound) {
          issues.push(
            `Enter an amount ${comparison.op === "atLeast" ? "at least as large as" : "no greater than"} “${source.label}”.`,
          );
        }
      } else if (
        fullDateSchema.safeParse(value.value).success &&
        fullDateSchema.safeParse(other.value).success
      ) {
        const years = comparison.years ?? 0;
        const bound = anniversary(other.value, years);
        const currentDate = Number(value.value.replaceAll("-", ""));
        const boundDate = Number(bound.replaceAll("-", ""));

        if (
          comparison.op === "atLeast"
            ? currentDate < boundDate
            : currentDate > boundDate
        ) {
          issues.push(
            years > 0
              ? `Choose a date at least ${years} calendar year${years === 1 ? "" : "s"} after “${source.label}”.`
              : `Choose a date ${comparison.op === "atLeast" ? "on or after" : "on or before"} “${source.label}”.`,
          );
        }
      }
    }
  }

  return issues;
}
